export function catalogCategory(product) {
  const text = [product.title, product.handle, product.type, ...(product.tags || [])].join(' ').toLowerCase();
  if (/key[\s-]*chain|\bcharms?\b/.test(text)) return 'keychains';
  if (/(?:pillow|dakimakura)[\s\w-]*(?:insert|inner)|(?:insert|inner)[\s\w-]*(?:pillow|dakimakura)/.test(text)) return 'inserts';
  if (/acrylic[\s-]*(?:art[\s-]*)?(?:board|frame)|art[\s-]*board/.test(text)) return 'art_boards';
  if (/standee/.test(text)) return 'standees';
  if (/laser[\s-]*ticket|art[\s-]*card|polaroid|holographic.*card/.test(text)) return 'art_cards';
  if (/desk[\s-]*mat|mouse[\s-]*pad/.test(text)) return 'deskmats';
  if (/pillowcase|pillow[\s-]*cover|dakimakura|body[\s-]*pillow/.test(text)) return 'pillowcases';
  if (/print|poster/.test(text)) return 'prints';
  return 'other';
}

export function catalogSeries(product) {
  const text = [product.title, product.handle, ...(product.tags || []), ...(product.collections || [])].join(' ').toLowerCase();
  const found = [];
  if (/genshin|\bgi\b/.test(text)) found.push('genshin');
  if (/honkai[:\s-]*star[\s-]*rail|\bhsr\b/.test(text)) found.push('hsr');
  if (/zenless[\s-]*zone[\s-]*zero|\bzzz\b/.test(text)) found.push('zzz');
  if (/wuthering[\s-]*waves|\bwuwa\b/.test(text)) found.push('wuwa');
  if (/love[\s-]*and[\s-]*deepspace|\blads\b/.test(text)) found.push('lads');
  if (/vtuber|v-tuber|virtual[\s-]*youtuber/.test(text)) found.push('vtuber');
  if (!found.length && /\banime\b/.test(text)) found.push('anime');
  return found.length ? found : ['other'];
}

export async function loadCatalogPages(initial, requestPage) {
  let current = initial;
  if (Number(current.page) !== 1) current = await requestPage(current.first);
  if (Number(current.page) !== 1 || !Array.isArray(current.items)) throw new Error('Invalid first catalog page');
  const products = new Map();
  const add = (items) => {
    for (const item of items) {
      if (item.id == null) throw new Error('Missing catalog product ID');
      products.set(String(item.id), item);
    }
  };
  add(current.items);
  const seen = new Set();
  while (current.next) {
    if (seen.has(current.next) || seen.size >= 100) throw new Error('Invalid catalog pagination');
    seen.add(current.next);
    const next = await requestPage(current.next);
    if (Number(next.page) !== Number(current.page) + 1 || !Array.isArray(next.items)) throw new Error('Catalog page did not advance');
    add(next.items);
    current = next;
  }
  return Array.from(products.values());
}

function initCatalog(root) {
  if (root.dataset.catalogReady) return;
  root.dataset.catalogReady = 'true';
  const labels = JSON.parse(root.querySelector('[data-catalog-labels]').textContent);
  const initial = JSON.parse(root.querySelector('[data-catalog-products]').textContent);
  const grid = root.querySelector('[data-catalog-grid]');
  const filters = root.querySelector('[data-catalog-filters]');
  const search = root.querySelector('[data-catalog-search]');
  const series = root.querySelector('[data-catalog-series]');
  const category = root.querySelector('[data-catalog-category]');
  const status = root.querySelector('[data-catalog-status]');
  const reset = root.querySelector('[data-catalog-reset]');
  const more = root.querySelector('[data-catalog-more]');
  const empty = root.querySelector('[data-catalog-empty]');
  const error = root.querySelector('[data-catalog-error]');
  const retry = root.querySelector('[data-catalog-retry]');
  const dialog = root.querySelector('[data-catalog-dialog]');
  const dialogImage = root.querySelector('[data-catalog-dialog-image]');
  const dialogImageError = root.querySelector('[data-catalog-image-error]');
  let products = new Map(initial.items.map((p) => [String(p.id), p]));
  let items = [];
  let limit = 36;
  let lastTrigger;
  let lastScrollY = 0;
  let loading = false;
  let searchTimer;
  const seriesName = (key) => labels['series_' + key] || labels.series_other;
  const seriesText = (keys) => keys.map(seriesName).join(' · ');
  const makeCard = (item, index) => {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'event-catalog__card';
    button.dataset.productId = String(item.id);
    const picture = document.createElement('span');
    picture.className = 'event-catalog__image';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = item.alt || item.title;
      img.width = 600;
      img.height = 600;
      img.loading = index < 2 ? 'eager' : 'lazy';
      if (index === 0) img.fetchPriority = 'high';
      picture.append(img);
    } else picture.textContent = labels.no_image;
    const name = document.createElement('span');
    name.className = 'event-catalog__name';
    name.textContent = item.title;
    const sub = document.createElement('span');
    sub.className = 'event-catalog__series';
    sub.textContent = seriesText(item.series);
    button.append(picture, name, sub);
    li.append(button);
    return li;
  };
  const render = (resetLimit = true) => {
    if (resetLimit) limit = 36;
    const terms = search.value.trim().normalize('NFKC').toLocaleLowerCase().split(/\s+/).filter(Boolean);
    const matched = items.filter((item) => terms.every((term) => item.search.includes(term)) && (!series.value || item.series.includes(series.value)) && (!category.value || category.value === item.category));
    const shown = matched.slice(0, limit);
    grid.replaceChildren(...shown.map(makeCard));
    status.textContent = labels.count.replace('__SHOWN__', String(shown.length)).replace('__TOTAL__', String(matched.length));
    more.hidden = matched.length <= shown.length;
    empty.hidden = matched.length !== 0;
    reset.hidden = !search.value && !series.value && !category.value;
  };
  const setItems = (raw) => {
    products = new Map(raw.map((p) => [String(p.id), p]));
    items = raw.map((product) => {
      const item = { ...product, series: catalogSeries(product), category: catalogCategory(product) };
      item.search = [product.title, product.handle, product.type, ...(product.tags || []), ...item.series.map(seriesName), labels['category_' + item.category]].join(' ').normalize('NFKC').toLocaleLowerCase();
      return item;
    }).sort((a, b) => seriesText(a.series).localeCompare(seriesText(b.series)) || a.title.localeCompare(b.title));
    const oldSeries = series.value;
    const oldCategory = category.value;
    series.replaceChildren(new Option(labels.all_series, ''));
    const seriesKeys = Array.from(new Set(items.flatMap((item) => item.series))).sort((a, b) => seriesName(a).localeCompare(seriesName(b)));
    for (const key of seriesKeys) series.append(new Option(seriesName(key), key));
    const order = ['pillowcases', 'prints', 'deskmats', 'standees', 'art_boards', 'art_cards', 'keychains', 'inserts', 'other'];
    category.replaceChildren(new Option(labels.all_categories, ''));
    for (const key of order) if (items.some((item) => item.category === key)) category.append(new Option(labels['category_' + key], key));
    series.value = seriesKeys.includes(oldSeries) ? oldSeries : '';
    category.value = order.includes(oldCategory) ? oldCategory : '';
    render();
  };
  const requestPage = async (path) => {
    const url = new URL(path, window.location.href);
    if (url.origin !== window.location.origin) throw new Error('Invalid catalog origin');
    url.searchParams.set('section_id', root.dataset.sectionId);
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Catalog request failed');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = html.querySelector('[data-catalog-products]');
    if (!data) throw new Error('Catalog page data missing');
    return JSON.parse(data.textContent);
  };
  const load = async () => {
    if (loading) return;
    loading = true;
    error.hidden = true;
    filters.disabled = true;
    status.textContent = labels.loading;
    try {
      setItems(await loadCatalogPages(initial, requestPage));
    } catch {
      if (!items.length) setItems(initial.items);
      error.hidden = false;
    } finally {
      loading = false;
      filters.disabled = false;
    }
  };
  grid.addEventListener('click', (event) => {
    const button = event.target.closest('[data-product-id]');
    if (!button) return;
    const item = products.get(button.dataset.productId);
    if (!item) return;
    lastTrigger = button;
    lastScrollY = window.scrollY;
    root.querySelector('[data-catalog-dialog-title]').textContent = item.title;
    root.querySelector('[data-catalog-dialog-series]').textContent = seriesText(catalogSeries(item));
    const image = item.large || item.image;
    dialogImage.hidden = !image;
    dialogImageError.hidden = Boolean(image);
    dialogImage.alt = item.alt || item.title;
    if (image) dialogImage.src = image;
    else dialogImage.removeAttribute('src');
    root.querySelector('[data-catalog-product-link]').href = item.url;
    dialog.showModal();
  });
  dialogImage.addEventListener('error', () => {
    dialogImage.hidden = true;
    dialogImageError.hidden = false;
  });
  root.querySelector('[data-catalog-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    dialogImage.removeAttribute('src');
    if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
    else search.focus({ preventScroll: true });
    window.scrollTo({ top: lastScrollY, behavior: 'instant' });
  });
  search.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => render(), 120);
  });
  series.addEventListener('change', () => render());
  category.addEventListener('change', () => render());
  reset.addEventListener('click', () => {
    search.value = '';
    series.value = '';
    category.value = '';
    render();
    search.focus({ preventScroll: true });
  });
  more.addEventListener('click', () => {
    const previousCount = grid.children.length;
    limit += 36;
    render(false);
    grid.children[previousCount]?.querySelector('button')?.focus({ preventScroll: true });
  });
  retry.addEventListener('click', load);
  load();
}

if (typeof document !== 'undefined') {
  const init = (scope) => scope.querySelectorAll('[data-event-catalog]').forEach(initCatalog);
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
}

